import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import CabIcon from "../components/cabinet/CabIcon";
import { Button, EmptyState, Skeleton } from "../components/ui";
import {
  getMySanctions, getMyAppeals, submitAppeal,
  getAppeal, sendAppealMessage,
} from "../api/sanctions";

const BAN_TYPE_LABEL = {
  user_timed_ban: "Мут на все действия",
  chat_ban: "Мут в чате игр",
  comment_ban: "Мут на комментарии",
  publish_ban: "Блок публикации игр",
};

export default function BanAppealsPage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  if (id === "new") return <Layout><NewForm sp={sp} nav={nav} /></Layout>;
  if (id) return <Layout><Detail appealId={id} nav={nav} /></Layout>;
  return <Layout><MyList /></Layout>;
}

/* Человекочитаемый статус жалобы. Логика была продублирована дословно
   в списке и в детальной — при правке одной формулировки вторая молча
   расходилась. `full` добавляет пояснение для страницы жалобы. */
export function appealStatusLabel(a, full = false) {
  if (a.status === "open") return "Подана";
  if (a.status === "discuss") return "В обсуждении";
  if (a.resolution === "unban") return full ? "Удовлетворена — бан снят" : "Удовлетворена";
  return "Отклонена";
}

/** Дата из ISO в «ДД.ММ ЧЧ:ММ». Раньше `.replace().slice()` повторялся 4 раза. */
function fmtDate(iso) {
  if (!iso) return "";
  return String(iso).replace("T", " ").slice(0, 16);
}

function MyList() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);

  /* Был `getMyAppeals().then(setItems)` без catch, а сам API глотает ошибку
     и возвращает []. Итог: при сбое сети страница уверенно сообщала
     «У вас нет жалоб» — то есть утверждала то, чего не проверяла. */
  const load = useCallback(() => {
    setItems(null);
    setError(false);
    getMyAppeals()
      .then((r) => {
        if (Array.isArray(r)) setItems(r);
        else setError(true);
      })
      .catch(() => setError(true));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div style={S.wrap}>
        <h1 style={S.h1}>Мои жалобы на бан</h1>
        <EmptyState
          title="Не получилось загрузить жалобы"
          desc="Проверь соединение и попробуй ещё раз."
          action={<Button variant="primary" onClick={load}>Повторить</Button>}
        />
      </div>
    );
  }

  if (items === null) {
    return (
      <div style={S.wrap}>
        <h1 style={S.h1}>Мои жалобы на бан</h1>
        <div style={{ display: "grid", gap: 10 }}>
          {[0, 1].map((i) => <Skeleton key={i} h={72} r={10} />)}
        </div>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <h1 style={S.h1}>Мои жалобы на бан</h1>
      {items.length === 0 && (
        <EmptyState
          title="Жалоб пока нет"
          desc="Если считаешь блокировку несправедливой — опиши ситуацию, МИН разберётся."
          action={
            <Button variant="primary" onClick={() => window.location.assign("/ban-appeals/new")}>
              Подать жалобу
            </Button>
          }
        />
      )}
      {items.map((a) => (
        <Link key={a.id} to={`/ban-appeals/${a.id}`} style={S.itemCard}>
          <div style={S.itemTitle}>
            {BAN_TYPE_LABEL[a.ban_type] || a.ban_type}
            {a.unread_for_user && <span style={S.unread}>новое</span>}
          </div>
          <div style={S.itemMeta}>
            Статус: <b>{appealStatusLabel(a)}</b>
            {a.last_msg_at && <> · {fmtDate(a.last_msg_at)}</>}
          </div>
        </Link>
      ))}
    </div>
  );
}

function NewForm({ sp, nav }) {
  const banType = sp.get("type") || "";
  const banId = parseInt(sp.get("id") || "0", 10);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState(null);
  useEffect(() => {
    getMySanctions().then((r) => {
      const s = (r.sanctions || []).find((x) => x.type === banType && x.id === banId);
      setInfo(s);
    });
  }, [banType, banId]);
  if (!BAN_TYPE_LABEL[banType] || !banId) {
    return <div style={S.wrap}>Некорректные параметры. <Link to="/app">В кабинет</Link></div>;
  }
  const submit = async () => {
    setErr("");
    if (text.trim().length < 10) { setErr("Опишите подробнее (мин 10 символов)."); return; }
    setBusy(true);
    const r = await submitAppeal({
      banType, banId, text, banSnapshot: info,
    });
    setBusy(false);
    if (r.ok && r.id) {
      nav(`/ban-appeals/${r.id}`);
    } else if (r.appeal_id) {
      nav(`/ban-appeals/${r.appeal_id}`);
    } else {
      setErr(r.message || "Не удалось подать жалобу.");
    }
  };
  return (
    <div style={S.wrap}>
      <Link to="/app" style={S.back}>← В кабинет</Link>
      <h1 style={S.h1}>Жалоба на бан</h1>
      <div style={S.subtitle}>
        Опишите ситуацию — МИН лично рассмотрит жалобу.
      </div>
      {info && (
        <div style={S.banInfo}>
          <div><b>Тип:</b> {BAN_TYPE_LABEL[banType]}</div>
          {info.reason && <div><b>Причина:</b> {info.reason}</div>}
          {info.banned_until && <div><b>До:</b> {info.banned_until.replace("T", " ").slice(0, 16)}</div>}
        </div>
      )}
      <textarea value={text} onChange={(e) => setText(e.target.value)}
                placeholder="Что произошло?" rows={8}
                style={S.textarea} maxLength={4000} />
      <div style={S.counter}>{text.length}/4000</div>
      {err && <div style={S.err}>{err}</div>}
      <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
        <button type="button" onClick={submit} disabled={busy} style={S.btnPrimary}>
          {busy ? "Отправляем…" : "Подать жалобу"}
        </button>
        <Link to="/app" style={S.btnSecondary}>Отмена</Link>
      </div>
      <div style={S.notice}>
        Бан можно обжаловать только один раз. После решения МИНа повторно нельзя.
      </div>
    </div>
  );
}

function Detail({ appealId, nav }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  /* Ошибки загрузки и отправки — разные вещи и показываются в разных
     местах: первая заменяет содержимое, вторая живёт рядом с полем ввода. */
  const [loadError, setLoadError] = useState(false);
  const [sendError, setSendError] = useState("");

  /* `if (r.ok) setData(r)` без ветки else оставлял `data === null`, и
     страница ВЕЧНО висела на «Загрузка…» — ни ошибки, ни способа повторить.
     Каждый тик поллинга (раз в 15 с) так же проваливался беззвучно. */
  const load = useCallback(async () => {
    try {
      const r = await getAppeal(appealId);
      if (r && r.ok) {
        setData(r);
        setLoadError(false);
      } else {
        setLoadError(true);
      }
    } catch {
      setLoadError(true);
    }
  }, [appealId]);

  useEffect(() => {
    /* Флаг актуальности вместо AbortController: getAppeal его не принимает,
       а без проверки ответ по старой жалобе мог прийти после переключения
       и перезаписать данные новой. */
    let current = true;
    const run = async () => {
      const r = await getAppeal(appealId).catch(() => null);
      if (!current) return;
      if (r && r.ok) { setData(r); setLoadError(false); }
      else setLoadError(true);
    };
    run();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") run();
    }, 15000);
    return () => { current = false; clearInterval(t); };
  }, [appealId]);

  if (!data) {
    if (loadError) {
      return (
        <div style={S.wrap}>
          <Link to="/ban-appeals" style={S.back}>← Мои жалобы</Link>
          <EmptyState
            title="Не получилось загрузить жалобу"
            desc="Проверь соединение и попробуй ещё раз."
            action={<Button variant="primary" onClick={load}>Повторить</Button>}
          />
        </div>
      );
    }
    return (
      <div style={S.wrap}>
        <Skeleton h={28} w="60%" />
        <Skeleton h={16} w="35%" style={{ marginTop: 12 }} />
        <Skeleton h={200} style={{ marginTop: 20, borderRadius: 10 }} />
      </div>
    );
  }

  const a = data.appeal;
  const closed = a.status === "resolved";

  /* Отправка сообщения. Раньше при `r.ok === false` не происходило ничего:
     кнопка отжималась, сообщение не появлялось, объяснения не было. */
  const send = async () => {
    if (text.trim().length === 0 || busy) return;
    setBusy(true);
    setSendError("");
    try {
      const r = await sendAppealMessage(appealId, text);
      if (r && r.ok) {
        setText("");
        await load();
      } else {
        setSendError("Не получилось отправить. Попробуй ещё раз.");
      }
    } catch {
      setSendError("Не получилось отправить. Попробуй ещё раз.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={S.wrap}>
      <Link to="/ban-appeals" style={S.back}>← Мои жалобы</Link>
      <h1 style={S.h1}>Жалоба #{a.id}: {BAN_TYPE_LABEL[a.ban_type] || a.ban_type}</h1>
      <div style={S.subtitle}>
        Статус: <b>{appealStatusLabel(a, true)}</b>
      </div>
      {a.ban_snapshot && (
        <div style={S.banInfo}>
          {a.ban_snapshot.reason && <div><b>Причина бана:</b> {a.ban_snapshot.reason}</div>}
          {a.ban_snapshot.banned_until && <div><b>До:</b> {fmtDate(a.ban_snapshot.banned_until)}</div>}
        </div>
      )}
      <div style={S.chat}>
        {data.messages.map((m) => <Msg key={m.id} m={m} />)}
      </div>
      {!closed && (
        <>
          <textarea value={text} onChange={(e) => setText(e.target.value)}
                    placeholder="Ваше сообщение…" rows={4}
                    style={S.textarea} maxLength={4000} />
          {sendError && <div style={S.sendErr}>{sendError}</div>}
          <Button variant="primary" onClick={send} loading={busy}>
            Отправить
          </Button>
        </>
      )}
      {closed && <div style={S.notice}>Жалоба закрыта. Решение МИНа окончательное.</div>}
    </div>
  );
}

function Msg({ m }) {
  if (m.is_system) {
    return <div style={{
      margin: "8px auto", padding: "8px 14px",
      background: "#f3f3f3", border: "1px solid #ddd",
      borderRadius: 8, color: "#555", fontSize: 13,
      fontStyle: "italic", textAlign: "center", maxWidth: 520,
    }}>{m.text}</div>;
  }
  const fromUser = m.author_kind === "user";
  return (
    <div style={{
      display: "flex", justifyContent: fromUser ? "flex-end" : "flex-start",
      marginBottom: 10,
    }}>
      <div style={{
        maxWidth: "75%", padding: "10px 14px", borderRadius: 12,
        background: fromUser ? "#e8f0ff" : "#fff8e1",
        border: fromUser ? "1px solid #4c97ff" : "1px solid #ffb74d",
        whiteSpace: "pre-wrap", wordBreak: "break-word",
      }}>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 4, fontWeight: 600 }}>
          {/* Была эмодзи-корона как метка роли: в каждой ОС выглядит
              по-своему и не красится в цвет темы. */}
          {fromUser ? "Вы" : <><CabIcon name="crown" size={13} /> МИН</>}
          {m.created_at && <span style={{ marginLeft: 8, fontWeight: 400 }}>{m.created_at.replace("T", " ").slice(0, 16)}</span>}
        </div>
        <div>{m.text}</div>
      </div>
    </div>
  );
}

const S = {
  wrap: { maxWidth: 720, margin: "20px auto", padding: 20, color: "#222831" },
  h1: { fontSize: 24, margin: "8px 0 6px" },
  subtitle: { color: "#555", fontSize: 14, marginBottom: 18, lineHeight: 1.5 },
  back: { color: "#3357FF", textDecoration: "none", fontSize: 13 },
  empty: { textAlign: "center", color: "#888", padding: 40 },
  itemCard: {
    display: "block", padding: "14px 18px", background: "#fff",
    border: "1px solid #E8EBF1", borderRadius: 12, marginBottom: 10,
    textDecoration: "none", color: "#222831",
  },
  itemTitle: { fontSize: 16, fontWeight: 700 },
  itemMeta: { fontSize: 13, color: "#666", marginTop: 4 },
  unread: { marginLeft: 8, fontSize: 11, padding: "2px 8px", background: "#ff5252", color: "#fff", borderRadius: 6 },
  banInfo: {
    background: "#fff5f5", border: "1px solid #ffd8d8",
    borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 14, lineHeight: 1.7,
  },
  textarea: {
    width: "100%", padding: 12, fontSize: 14,
    border: "1px solid #d0d4dc", borderRadius: 10,
    boxSizing: "border-box", resize: "vertical", fontFamily: "inherit",
  },
  counter: { fontSize: 12, color: "#888", textAlign: "right", marginTop: 4 },
  err: { color: "#c00", fontSize: 13, marginTop: 8 },
  sendErr: {
    marginBottom: 10, padding: "9px 13px", borderRadius: 8,
    background: "var(--danger-bg)", border: "1px solid var(--danger)",
    color: "var(--danger)", fontSize: 13, fontWeight: 600,
  },
  btnPrimary: {
    padding: "12px 24px", background: "#3357FF", color: "#fff",
    border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer",
  },
  btnSecondary: {
    padding: "12px 24px", background: "#fff", color: "#222",
    border: "1px solid #d0d4dc", borderRadius: 10, fontSize: 14,
    textDecoration: "none", display: "inline-flex", alignItems: "center",
  },
  notice: {
    marginTop: 14, padding: "10px 14px",
    background: "#fff8e1", border: "1px solid #ffd180",
    borderRadius: 8, fontSize: 13, color: "#5d4500",
  },
  chat: {
    margin: "18px 0", padding: 12,
    background: "#fafbfd", border: "1px solid #e8ebf1",
    borderRadius: 12, minHeight: 200, maxHeight: 500, overflowY: "auto",
  },
};
